import { useEffect } from "react"
import type { MutableRefObject } from "react"
import type { WebViewerInstance } from "@pdftron/webviewer"
import { bindPdfLinkRemoval } from "../core/pdfLinks"

type Props = {
	instanceRef: MutableRefObject<WebViewerInstance | null>
	isInitialized: boolean
	// Bumped when Apryse re-mounts the document area (split enter/exit): the second viewer is new.
	layoutEpoch: number
}

/**
 * Keeps the PDF's own link annotations out of every document viewer so a click can't navigate away.
 * Binds the viewers directly, not via `getViewerPanes`: that skips a pane until its scroll element
 * is attached, which can be after the first document has already imported its links.
 */
export const useRemovePdfLinks = ({ instanceRef, isInitialized, layoutEpoch }: Props) => {
	useEffect(() => {
		const instance = instanceRef.current
		if (!isInitialized || !instance) return undefined
		const viewers = instance.Core.getDocumentViewers?.() ?? [instance.Core.documentViewer]
		const unbinds = viewers.map((documentViewer) => bindPdfLinkRemoval(documentViewer, instance.Core.Annotations.Link))
		return () => unbinds.forEach((unbind) => unbind())
	}, [isInitialized, instanceRef, layoutEpoch])
}
